/**
 * 视频播放器控制器 - 专门处理视频播放
 * 从 HybridAudioController 中提取视频相关功能
 */
import * as tokenService from '@/utils/tokenService';
import { extractSongIdFromUrl } from '@/utils/audioMD5';
import unifiedVideoCacheManager, { isVideoSongAutoCacheEnabled } from '@/services/UnifiedVideoCacheManager.js';
import myRequest from '@/utils/myRequest';
import {
  prepareHlsStreams,
  buildHlsMasterPlaylist as buildAliyunHlsMasterUrl,
  resolveAlbumHlsSource,
  createHlsJsConfig,
  bindAliyunHlsNetworkFallback,
  _setHlsModuleRef
} from '@/utils/aliyunHlsPlayback';
import { EventEmitter } from 'events';

/**
 * 阿里云视频版歌曲：是否走 HLS 转码流。
 * true（默认）= 绑定阿里云盘时优先 HLS，转码未完成或失败时回退原文件直链 Range。
 * false = 始终原文件直链 Range（清晰度更高，但易受原文件下载限速影响）。
 */
export const ALIYUN_VIDEO_HLS_PLAYBACK_ENABLED = true;

class VideoController extends EventEmitter {
  constructor() {
    super(); // 调用父类构造函数
    // 绑定事件处理方法
    this.handleTimeUpdate = this.handleTimeUpdate.bind(this);
    this.handleEnded = this.handleEnded.bind(this);
    this.handlePlay = this.handlePlay.bind(this);
    this.handlePause = this.handlePause.bind(this);
    this.handleError = this.handleError.bind(this);
    this.handleLoadedMetadata = this.handleLoadedMetadata.bind(this);
    this.handleCanPlay = this.handleCanPlay.bind(this);
    this.handleProgress = this.handleProgress.bind(this);
    this.handleWaiting = this.handleWaiting.bind(this);
    this.handlePlaying = this.handlePlaying.bind(this);
    this.handlePageUnload = this.handlePageUnload.bind(this);
    
    // 视频元素
    this.video = null;
    this.currentUserId = null;
    this.currentSongUrl = null;
    this.currentSongId = null;
    this.currentBlobUrl = null;
    
    // 视频缓存相关
    this.videoCacheStarted = false;
    this.videoCacheProgress = 0;
    this.videoCacheStatus = '';
    this.isVideoLoading = false;
    this.isVideoCaching = false;
    
    // 流式播放相关
    this.streamingTimeout = null;
    this.retryCount = 0;
    this.maxRetries = 3;
    
    // 🔥 新增：取消标志，用于防止在切换歌曲时旧策略继续加载和播放
    this.isCancelled = false;
    this.currentCanPlayHandlers = []; // 存储所有 canplay 监听器，方便统一移除
    /** 当前曲目是否已向策略层上报过 canplay（避免 re-buffer 时重复触发自动播放逻辑） */
    this._canplayEmittedForCurrentSong = false;
    /** HLS（hls.js）实例与状态 */
    this._hls = null;
    this._usingHls = false;
    this._hlsMasterBlobUrl = null;
    /** 是否正在加载新视频源（期间忽略 <video> 的瞬时 error，避免切歌重试雪崩） */
    this._loadingNewSource = false;
    
    // 事件回调集合
    this.eventCallbacks = {
      timeupdate: [],
      ended: [],
      play: [],
      pause: [],
      error: [],
      loadedmetadata: [],
      canplay: [],
      progress: [],
      waiting: [],
      playing: [],
      videoloading: [],
      videocaching: []
    };
    
    // 绑定页面卸载事件
    window.addEventListener('beforeunload', this.handlePageUnload);
  }

  // 获取或创建视频元素
  getVideoElement() {
    // 🎬 优先使用LyricPlayer中的视频元素
    const lyricPlayerVideo = document.querySelector('.video-player');
    console.log('🎬 [VideoController] 查找视频元素:', lyricPlayerVideo);
    
    if (lyricPlayerVideo) {
      console.log('🎬 [VideoController] 使用LyricPlayer中的视频元素');
      this.video = lyricPlayerVideo;
      return this.video;
    }
    
    // 如果没有找到，等待一段时间后重试
    console.log('🎬 [VideoController] 视频元素未找到，创建新的视频元素');
    
    // 如果没有找到，创建新的视频元素
    if (this.video) {
      // 如果已经有视频元素，先清理
      try {
        if (this.video.parentNode) {
          this.video.parentNode.removeChild(this.video);
        }
      } catch (error) {
        console.warn('🎬 [VideoController] 清理旧视频元素时出错:', error);
      }
    }
    
    this.video = document.createElement('video');
    this.video.className = 'video-player'; // 🔥 添加类名，方便后续查找
    this.video.preload = 'auto';
    this.video.muted = true;
    this.video.loop = true;
    this.video.playsInline = true;
    this.video.crossOrigin = 'anonymous';
    this.video.style.display = 'none'; // 🔥 隐藏创建的视频元素，避免影响UI
    
    // 🔥 将视频元素添加到 body，确保它可以正常工作
    document.body.appendChild(this.video);
    console.log('🎬 [VideoController] 已将视频元素添加到 DOM');
    
    // 绑定所有事件监听器 - 使用箭头函数确保this绑定正确
    this.video.addEventListener('timeupdate', () => this.handleTimeUpdate());
    this.video.addEventListener('ended', () => this.handleEnded());
    this.video.addEventListener('play', () => this.handlePlay());
    this.video.addEventListener('pause', () => this.handlePause());
    this.video.addEventListener('error', () => this.handleError());
    this.video.addEventListener('loadedmetadata', () => this.handleLoadedMetadata());
    // 🎬 移除全局canplay监听器，避免与临时监听器冲突
    // this.video.addEventListener('canplay', () => this.handleCanPlay());
    this.video.addEventListener('progress', () => this.handleProgress());
    this.video.addEventListener('waiting', () => this.handleWaiting());
    this.video.addEventListener('playing', () => this.handlePlaying());
    this.video.addEventListener('loadstart', () => this.handleLoadStart());
    // 🎬 移除全局canplaythrough监听器，避免重复处理
    // this.video.addEventListener('canplaythrough', () => this.handleCanPlayThrough());
    
    console.log('🎬 [VideoController] 创建新的视频元素并添加到DOM');
    return this.video;
  }

  // 异步等待视频元素
  async waitForVideoElement(maxRetries = 300, delay = 100) {
    for (let i = 0; i < maxRetries; i++) {
      const lyricPlayerVideo = document.querySelector('.video-player');
      if (lyricPlayerVideo) {
        console.log('🎬 [VideoController] 找到LyricPlayer视频元素');
        this.video = lyricPlayerVideo;
        
        // 🔥 新增：确保视频元素状态干净
        this.resetVideoElementState();
        
        // 🔥 关键修复：找到已存在的视频元素后，必须重新绑定事件监听器
        // 确保 playing 事件能够正确触发并更新全局状态
        this.rebindEventListeners();
        
        return this.video;
      }
      
      console.log(`🎬 [VideoController] 等待视频元素... (${i + 1}/${maxRetries})`);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
    
    console.log('🎬 [VideoController] 未找到LyricPlayer视频元素，使用创建的元素');
    
    // 🔥 修复：如果没有找到DOM元素且 this.video 为 null，创建新的视频元素
    if (!this.video) {
      console.log('🎬 [VideoController] this.video 为 null，创建新的视频元素');
      this.video = this.getVideoElement();
    }
    
    // 🔥 新增：如果使用创建的元素，也要确保状态干净
    if (this.video) {
      this.resetVideoElementState();
      // 创建的元素在 getVideoElement() 中已经绑定了事件监听器，这里不需要重复绑定
    }
    
    return this.video;
  }

  // 🔥 新增：重置视频元素状态
  resetVideoElementState() {
    if (this.video) {
      console.log('🎬 [VideoController] 重置视频元素状态');
      
      // 停止播放
      this.video.pause();
      this.video.currentTime = 0;
      
      // 清空源
      this.video.src = '';
      
      // 重置视频元素
      this.video.load();
      
      // 重置属性
      this.video.volume = 1.0;
      this.video.muted = false;
      
      console.log('✅ [VideoController] 视频元素状态重置完成');
    }
  }

  // 🔥 新增：重新绑定事件监听器
  rebindEventListeners() {
    if (!this.video) {
      console.warn('🎬 [VideoController] 没有视频元素，无法绑定事件监听器');
      return;
    }

    console.log('🎬 [VideoController] 重新绑定事件监听器');
    
    try {
      // 🔥 关键修复：先移除可能存在的旧监听器，避免重复绑定
      // 移除所有事件监听器（使用相同的函数引用）
      this.video.removeEventListener('timeupdate', this.handleTimeUpdate);
      this.video.removeEventListener('ended', this.handleEnded);
      this.video.removeEventListener('play', this.handlePlay);
      this.video.removeEventListener('pause', this.handlePause);
      this.video.removeEventListener('error', this.handleError);
      this.video.removeEventListener('loadedmetadata', this.handleLoadedMetadata);
      this.video.removeEventListener('canplay', this.handleCanPlay);
      this.video.removeEventListener('progress', this.handleProgress);
      this.video.removeEventListener('waiting', this.handleWaiting);
      this.video.removeEventListener('playing', this.handlePlaying);
      
      // 重新绑定所有事件监听器
      this.video.addEventListener('timeupdate', this.handleTimeUpdate);
      this.video.addEventListener('ended', this.handleEnded);
      this.video.addEventListener('play', this.handlePlay);
      this.video.addEventListener('pause', this.handlePause);
      this.video.addEventListener('error', this.handleError);
      this.video.addEventListener('loadedmetadata', this.handleLoadedMetadata);
      this.video.addEventListener('canplay', this.handleCanPlay);
      this.video.addEventListener('progress', this.handleProgress);
      this.video.addEventListener('waiting', this.handleWaiting);
      // 🔥 关键：确保 playing 事件监听器正确绑定
      this.video.addEventListener('playing', this.handlePlaying);
      
      console.log('✅ [VideoController] 事件监听器重新绑定完成（包括 playing 事件）');
    } catch (error) {
      console.error('❌ [VideoController] 绑定事件监听器失败:', error);
    }
  }

  /**
   * 加载视频歌曲
   * @param {string|number} urlOrSongId - 视频URL或歌曲ID（推荐直接使用songId）
   * @param {number|Object} startTimeOrOptions - 开始时间（秒）或选项对象
   * @returns {Promise<boolean>}
   */
  async loadVideoSong(urlOrSongId, startTimeOrOptions = 0) {
    console.log('🎬 [VideoController] ===== loadVideoSong 方法开始 =====');
    
    try {
      // 🔥 新增：彻底清理前一首歌的资源
      await this.cleanupPreviousSong();
      
      // 🔥 重置取消标志，允许新歌曲加载
      this.isCancelled = false;
      // 🔥 标记正在加载新源：期间 <video> 因清空/切换 src 产生的瞬时 error 不外抛
      this._loadingNewSource = true;
      console.log('✅ [VideoController] 重置取消标志，允许新歌曲加载');
      
      // 解析参数
      const startTime = typeof startTimeOrOptions === 'number' ? startTimeOrOptions : (startTimeOrOptions.startTime || 0);
      const options = typeof startTimeOrOptions === 'object' ? startTimeOrOptions : {};
      const videoUrl = options.videoUrl;
      const hlsStreams = options.hlsStreams;
      
      // 获取或创建视频元素，等待DOM渲染
      await this.waitForVideoElement();
      
      // 🔥 新增：重新绑定事件监听器
      this.rebindEventListeners();

      // 分享场景：直接使用后端返回的 HLS 转码流（受 ALIYUN_VIDEO_HLS_PLAYBACK_ENABLED 控制）
      if (
        ALIYUN_VIDEO_HLS_PLAYBACK_ENABLED
        && hlsStreams
        && Array.isArray(hlsStreams)
        && hlsStreams.length > 0
      ) {
        console.log('🎬 [VideoController] 分享场景：使用提供的 HLS 转码流');
        await this.playFromHls(hlsStreams, startTime, { deferAutoPlay: true });
        return true;
      }
      
      // 分享场景：直接使用提供的视频直链
      if (videoUrl) {
        console.log('🎬 [VideoController] 分享场景：直接使用提供的视频直链:', videoUrl.substring(0, 50) + '...');
        // 获取用户ID（用于缓存）
        const userId = tokenService.getCurrentUserId();
        
        // 获取歌曲ID（用于缓存和验证）
        let songId;
        if (typeof urlOrSongId === 'number' || (typeof urlOrSongId === 'string' && /^\d+$/.test(urlOrSongId))) {
          songId = String(urlOrSongId);
        } else {
          songId = extractSongIdFromUrl(urlOrSongId);
        }
        
        if (songId) {
          this.currentSongId = songId;
          this.currentUserId = userId;
          
          // 先检查缓存
          const videoCacheResult = await this.checkVideoCache(userId, songId);
          if (videoCacheResult) {
            console.log('🎬 [VideoController] 找到视频缓存，使用缓存:', videoCacheResult);
            await this.playFromVideoCache(videoCacheResult, startTime);
            return true;
          }
        }
        
        // 没有缓存，直接使用提供的视频直链播放
        console.log('🎬 [VideoController] 使用分享场景的视频直链播放');
        await this.playFromOneDrive(videoUrl, startTime);
        return true;
      }
      
      // 获取用户ID和歌曲ID
      const userId = tokenService.getCurrentUserId();
      
      // 🔥 优化：支持直接传递 songId（数字或字符串），也兼容 URL 格式
      let songId;
      if (typeof urlOrSongId === 'number' || (typeof urlOrSongId === 'string' && /^\d+$/.test(urlOrSongId))) {
        // 直接传递的是 songId
        songId = String(urlOrSongId);
        console.log('🎬 [VideoController] 直接使用歌曲ID:', songId);
      } else {
        // 传递的是 URL，需要从 URL 中提取 songId
        songId = extractSongIdFromUrl(urlOrSongId);
        console.log('🎬 [VideoController] 从URL提取歌曲ID:', urlOrSongId, '->', songId);
      }
      
      if (!songId) {
        throw new Error(`无法获取歌曲ID (urlOrSongId: ${urlOrSongId})`);
      }
      
      console.log('🎬 [VideoController] 用户ID:', userId);
      console.log('🎬 [VideoController] 歌曲ID:', songId);
      
      // 🔥 保存当前加载的歌曲ID，用于验证视频源是否匹配
      this.currentSongId = songId;
      this.currentUserId = userId;
      this._canplayEmittedForCurrentSong = false;
      
      // 检查视频缓存
      const videoCacheResult = await this.checkVideoCache(userId, songId);
      if (videoCacheResult) {
        console.log('🎬 [VideoController] 找到视频缓存:', videoCacheResult);
        await this.playFromVideoCache(videoCacheResult, startTime);
        
        console.log('🎬 [VideoController] 视频缓存播放完成');
        return true;
      }
      
      const storageType = options.storageType;
      const isOneDriveStorage = storageType === 1 || storageType === '1';

      // 🥇 阿里云盘：HLS 转码流优先（OneDrive 存储跳过；开关关闭时整段跳过）
      if (ALIYUN_VIDEO_HLS_PLAYBACK_ENABLED && !isOneDriveStorage) {
        const aliyunPreview = await this.getAliyunVideoPreview(songId);
        if (this.isCancelled || this.currentSongId !== songId) {
          throw new Error('视频加载已取消或歌曲已切换');
        }
        if (aliyunPreview) {
          if (aliyunPreview.playMethod === 'hls' && Array.isArray(aliyunPreview.hlsStreams) && aliyunPreview.hlsStreams.length > 0) {
            try {
              await this.playFromHls(aliyunPreview.hlsStreams, startTime, { deferAutoPlay: true });
              console.log('🎬 [VideoController] 阿里云 HLS 转码流加载完成');
              return true;
            } catch (hlsError) {
              console.warn('🎬 [VideoController] HLS 播放失败，回退阿里云原文件直链:', hlsError?.message);
              if (this.isCancelled || this.currentSongId !== songId) throw hlsError;
              this.destroyHls();
            }
          }
          if (aliyunPreview.directUrl) {
            console.log('🎬 [VideoController] 阿里云转码未完成，使用原文件直链');
            await this.playFromDirectUrl(aliyunPreview.directUrl, startTime, {
              noReferrer: true,
              deferAutoPlay: true
            });
            return true;
          }
        }
      }

      // 🥇 原文件直链：<video> 直连 CDN，浏览器自动 Range 分段（HLS 关闭时的主路径）
      const originalDirect = await this.resolveVideoOriginalDirectUrl(songId, options);
      if (this.isCancelled || this.currentSongId !== songId) {
        throw new Error('视频加载已取消或歌曲已切换');
      }
      if (originalDirect?.directUrl) {
        const st = originalDirect.storageType ?? storageType;
        const needsNoReferrer = st === 2 || st === '2';
        console.log(
          '🎬 [VideoController] 使用原文件直链（浏览器 Range 分段）:',
          originalDirect.directUrl.substring(0, 80) + '...'
        );
        await this.playFromDirectUrl(originalDirect.directUrl, startTime, {
          noReferrer: needsNoReferrer,
          deferAutoPlay: true
        });
        console.log('🎬 [VideoController] 原文件直链视频加载完成');
        return true;
      }

      // 兼容旧路径：OneDrive 状态检查 + onedrive-direct-url
      console.log('🎬 [VideoController] 播放直链不可用，尝试 OneDrive 兼容路径');
      const oneDriveVideoUrl = await this.getOneDriveVideoUrl(songId);

      if (this.isCancelled || this.currentSongId !== songId) {
        throw new Error('视频加载已取消或歌曲已切换');
      }

      if (oneDriveVideoUrl) {
        console.log('🎬 [VideoController] 使用 OneDrive 视频 URL:', oneDriveVideoUrl);
        await this.playFromOneDrive(oneDriveVideoUrl, startTime);
        return true;
      }
      
      // 最后回退到本地流式接口
      console.log('🎬 [VideoController] OneDrive视频获取失败，回退到本地流式接口');
      const localStreamUrl = this.buildAuthenticatedStreamUrl(songId);
      const result = await this.playFromCDN(localStreamUrl, startTime);
      
      // 🔥 修复：playFromCDN 已经在 canplay 事件中自动播放了，不需要重复调用
      // 这里只需要等待 Promise resolve，表示视频已经准备好并开始播放
      if (this.isCancelled) {
        console.log('🚫 [VideoController] 视频加载已取消');
      }
      
      return result;
      
    } catch (error) {
      console.error('🎬 [VideoController] loadVideoSong 失败:', error);
      this._loadingNewSource = false;
      // 仅在不是“取消/切歌”导致的失败时才外抛错误，避免切歌的取消异常触发上层重试
      if (!this.isCancelled) {
        this.emit('error', { error: error.message });
      }
      throw error;
    }
  }

  // 检查视频缓存
  async checkVideoCache(userId, songId) {
    try {
      console.log('🎬 [VideoController] 检查视频缓存:', { userId, songId, songIdType: typeof songId });
      const cacheResult = await unifiedVideoCacheManager.getCachedVideo(userId, songId);
      
      if (cacheResult && cacheResult.blobUrl) {
        console.log('✅ [VideoController] 找到视频缓存，使用 DLOB 地址:', cacheResult.blobUrl, {
          userId,
          songId,
          cacheSize: cacheResult.size,
          cacheType: cacheResult.type
        });
        // 🔧 修复：将 blobUrl 映射为 videoUrl 以保持兼容性
        return {
          ...cacheResult,
          videoUrl: cacheResult.blobUrl
        };
      }
      
      console.log('❌ [VideoController] 未找到视频缓存:', { userId, songId, songIdType: typeof songId });
      return null;
    } catch (error) {
      console.error('🎬 [VideoController] 检查视频缓存失败:', error, { userId, songId });
      return null;
    }
  }

  /**
   * 构建带鉴权 token 的本地流式 URL（<video> 无法携带 Authorization 头）
   */
  buildAuthenticatedStreamUrl(songId) {
    const token = tokenService.getAccessToken();
    const base = `/api/music/${songId}/stream`;
    return token ? `${base}?token=${encodeURIComponent(token)}` : base;
  }

  /**
   * 解析视频原文件 CDN 直链（供 <video> Range 播放）。
   * 阿里云优先走 /api/video/aliyun-download-url，否则回退 playback-direct-url。
   */
  async resolveVideoOriginalDirectUrl(songId, { videoId, storageType } = {}) {
    const isOneDrive = storageType === 1 || storageType === '1';

    if (!isOneDrive && videoId != null) {
      try {
        const resp = await myRequest.get(`/api/video/aliyun-download-url/${videoId}`, {
          params: { musicId: songId }
        });
        const url = resp?.directUrl || resp?.url;
        if (url) {
          return { directUrl: url, storageType: 2 };
        }
      } catch (error) {
        console.warn('🎬 [VideoController] 阿里云视频原文件直链获取失败，回退 playback-direct-url:', error?.message || error);
      }
    }

    return this.getPlaybackDirectUrl(songId);
  }

  /**
   * 获取播放用 CDN 直链（OneDrive / 阿里云盘），不经 /stream
   */
  async getPlaybackDirectUrl(songId) {
    try {
      const response = await myRequest.get(`/api/music/playback-direct-url/${songId}`);
      if (response && response.directUrl) {
        return {
          directUrl: response.directUrl,
          storageType: response.storageType
        };
      }
      return null;
    } catch (error) {
      console.warn('🎬 [VideoController] 获取播放直链失败:', error);
      return null;
    }
  }

  /**
   * 使用 CDN 直链播放（与 playFromOneDrive 相同，可选 no-referrer 供阿里云 CDN）
   */
  async playFromDirectUrl(directUrl, startTime = 0, { noReferrer = false, deferAutoPlay = false } = {}) {
    if (noReferrer && this.video) {
      this.video.setAttribute('referrerpolicy', 'no-referrer');
    }
    return this.playFromOneDrive(directUrl, startTime, { deferAutoPlay });
  }

  /**
   * 获取阿里云盘视频 HLS 转码播放信息（多清晰度自适应流，不受原文件下载限速影响）。
   * 非阿里云盘存储后端返回 400 → 这里捕获后返回 null，调用方回退通用直链逻辑。
   * @returns {Promise<{playMethod:string, hlsStreams?:Array, directUrl?:string}|null>}
   */
  async getAliyunVideoPreview(songId) {
    try {
      const response = await myRequest.get(`/api/music/aliyun-video-preview/${songId}`);
      if (response && response.playMethod) {
        return response;
      }
      return null;
    } catch (error) {
      // 非阿里云盘视频（400）或转码接口异常，静默回退
      console.log('🎬 [VideoController] 阿里云 HLS 播放信息不可用，回退通用直链');
      return null;
    }
  }

  /**
   * 由阿里云返回的多清晰度变体 m3u8 构建 HLS master playlist（Blob URL）
   * @param {Array<{templateId:string,url:string,width:number,height:number}>} streams
   * @returns {string} master playlist 的 Blob URL
   */
  buildHlsMasterPlaylist(streams) {
    const prepared = prepareHlsStreams(streams);
    const blobUrl = buildAliyunHlsMasterUrl(prepared);
    this._hlsMasterBlobUrl = blobUrl;
    return blobUrl;
  }

  /** 销毁 hls.js 实例并释放 master playlist Blob URL */
  destroyHls() {
    if (this._hls) {
      try { this._hls.destroy(); } catch (_) { /* ignore */ }
      this._hls = null;
    }
    if (this._hlsMasterBlobUrl) {
      // 原生 HLS 场景：<video>.src 仍指向该 master blob，撤销前先摘除，避免悬空引用报错
      try {
        if (this.video && this.video.src === this._hlsMasterBlobUrl) {
          this.video.removeAttribute('src');
          this.video.load();
        }
      } catch (_) { /* ignore */ }
      try { URL.revokeObjectURL(this._hlsMasterBlobUrl); } catch (_) { /* ignore */ }
      this._hlsMasterBlobUrl = null;
    }
    this._usingHls = false;
  }

  /**
   * 用 HLS 播放阿里云转码流：固定最高清晰度单路 m3u8，禁止 ABR 换档（与图集一致）。
   * @param {Array} streams 多清晰度变体（至少一项含 url）
   * @param {number} startTime
   * @param {{deferAutoPlay?:boolean}} options deferAutoPlay 时只就绪不自动播放，交由策略层统一 play()
   */
  async playFromHls(streams, startTime = 0, { deferAutoPlay = true, cacheCtx = null } = {}) {
    if (this.isCancelled) throw new Error('视频加载已取消');
    if (!this.video) {
      this.getVideoElement();
      if (!this.video) throw new Error('无法获取视频元素');
    }

    const expectedSongId = this.currentSongId;
    // 分片缓存：仅自动缓存开启时边播边存（手动缓存走整文件下载，不受 HLS 分片影响）
    const effectiveCacheCtx = isVideoSongAutoCacheEnabled()
      ? (cacheCtx || (expectedSongId != null ? { videoId: `m${expectedSongId}` } : null))
      : null;

    // 清理上一条 HLS/普通源
    this.destroyHls();
    await this.clearVideoSrcBeforeLoad(null);
    if (this.isCancelled || this.currentSongId !== expectedSongId) {
      throw new Error('视频加载已取消或歌曲已切换');
    }

    this._canplayEmittedForCurrentSong = false;
    this.currentSongUrl = null;
    this._usingHls = true;

    this.video.setAttribute('referrerpolicy', 'no-referrer');

    const preparedStreams = prepareHlsStreams(streams);
    const sourceInfo = resolveAlbumHlsSource(preparedStreams);
    if (sourceInfo.masterBlobUrl) {
      this._hlsMasterBlobUrl = sourceInfo.masterBlobUrl;
    }
    const loadUrl = sourceInfo.url;
    console.log(
      '🎬 [VideoController] 阿里云 HLS',
      sourceInfo.mode,
      '固定最高档:',
      sourceInfo.streams.map((s) => `${s.templateId}(${s.width}x${s.height})`).join(', ') || loadUrl
    );

    const Hls = (await import('hls.js')).default;
    if (this.isCancelled || this.currentSongId !== expectedSongId) {
      this.destroyHls();
      throw new Error('视频加载已取消或歌曲已切换');
    }

    if (Hls.isSupported()) {
      console.log('🎬 [VideoController] 使用 hls.js 固定最高清晰度');
      _setHlsModuleRef(Hls);
      const hls = new Hls(createHlsJsConfig(sourceInfo.startLevelIndex, {
        cacheCtx: effectiveCacheCtx,
        lockLevel: true
      }));
      this._hls = hls;
      hls.attachMedia(this.video);
      hls.loadSource(loadUrl);
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        hls.loadLevel = 0;
        hls.currentLevel = 0;
        const level = hls.levels?.[0];
        console.log(
          `🎬 [VideoController] 锁定档位 index=0, ${level?.width || '?'}x${level?.height || '?'}`
        );
      });
      bindAliyunHlsNetworkFallback(hls, Hls, sourceInfo, '🎬 [VideoController]', (data) => {
        if (data.fatal) {
          console.error('🎬 [VideoController] HLS 致命错误:', data.type, data.details);
          this.emit('error', { error: `HLS错误: ${data.details}` });
        } else if (data.details === 'bufferStalledError') {
          console.warn('🎬 [VideoController] 缓冲卡住（已锁定最高档，不会降码率）');
        } else if (!['levelLoadTimeOut', 'manifestLoadTimeOut', 'fragLoadTimeOut'].includes(data.details)) {
          console.warn('🎬 [VideoController] HLS 非致命错误:', data.details);
        }
      });
      if (startTime > 0) this.video.currentTime = startTime;
      return this._waitHlsReady(expectedSongId, startTime, deferAutoPlay);
    }

    // 2) 回退 Safari/iOS 原生 HLS（清晰度见 aliyunHlsPlayback ALIYUN_HLS_QUALITY）
    const canNative = this.video.canPlayType('application/vnd.apple.mpegurl');
    if (canNative) {
      console.log('🎬 [VideoController] 使用原生 HLS（iOS/Safari）');
      this.video.src = loadUrl;
      this.video.load();
      if (startTime > 0) this.video.currentTime = startTime;
      return this._waitHlsReady(expectedSongId, startTime, deferAutoPlay);
    }

    this.destroyHls();
    throw new Error('当前浏览器不支持 HLS');
  }

  /** 等待 HLS 就绪（canplay）后 resolve；deferAutoPlay=false 时顺带自动播放 */
  _waitHlsReady(expectedSongId, startTime, deferAutoPlay) {
    return new Promise((resolve, reject) => {
      if (this.isCancelled) { reject(new Error('视频加载已取消')); return; }

      const cleanup = () => {
        this.video.removeEventListener('canplay', onCanPlay);
        this.video.removeEventListener('error', onError);
        const idx = this.currentCanPlayHandlers.indexOf(onCanPlay);
        if (idx > -1) this.currentCanPlayHandlers.splice(idx, 1);
      };

      const onCanPlay = async () => {
        if (this.isCancelled || this.currentSongId !== expectedSongId) {
          cleanup();
          reject(new Error('视频加载已取消或歌曲已切换'));
          return;
        }
        try {
          if (deferAutoPlay) {
            console.log('🎬 [VideoController] HLS 视频可播放，交由 PlaybackStrategyManager 统一 play()');
          } else {
            await this.play();
          }
          resolve();
        } catch (e) {
          reject(e);
        } finally {
          cleanup();
        }
      };

      const onError = () => {
        cleanup();
        reject(new Error('HLS 视频加载失败'));
      };

      this.video.addEventListener('canplay', onCanPlay);
      this.video.addEventListener('error', onError);
      this.currentCanPlayHandlers.push(onCanPlay);

      if (this.video.readyState >= 3 && !this.isCancelled) {
        onCanPlay();
      }
    });
  }

  /** @deprecated 请使用 playFromDirectUrl + getPlaybackDirectUrl，不再经 /stream */
  async playFromAliyun(streamUrl, startTime = 0) {
    try {
      if (this.isCancelled) throw new Error('视频加载已取消');
      if (!this.video) {
        this.getVideoElement();
        if (!this.video) throw new Error('无法获取视频元素');
      }

      const expectedSongId = this.currentSongId;
      this.currentSongUrl = streamUrl;

      if (this.isCancelled || this.currentSongId !== expectedSongId) {
        throw new Error('视频加载已取消或歌曲已切换');
      }

      await this.clearVideoSrcBeforeLoad(streamUrl);
      this._canplayEmittedForCurrentSong = false;

      // 确保 referrerpolicy 属性生效（HTML 模板里已有，这里 JS 再加一层保险）
      this.video.setAttribute('referrerpolicy', 'no-referrer');
      // 直接设 stream URL，<video> 跟随 302 到 CDN，原生流式播放，不经过服务器
      this.video.src = streamUrl;
      this.video.load();
      console.log('🎬 [VideoController] 阿里云盘视频 stream URL 已设置（带 no-referrer）:', streamUrl);

      if (startTime > 0) this.video.currentTime = startTime;

      return new Promise((resolve, reject) => {
        if (this.isCancelled) { reject(new Error('视频加载已取消')); return; }

        const handleCanPlay = async () => {
          if (this.isCancelled || this.currentSongId !== expectedSongId) {
            this.video.removeEventListener('canplay', handleCanPlay);
            reject(new Error('视频加载已取消或歌曲已切换'));
            return;
          }
          try {
            await this.play();
            console.log('🎬 [VideoController] 阿里云盘视频自动播放成功');
            resolve();
          } catch (e) {
            reject(e);
          } finally {
            this.video.removeEventListener('canplay', handleCanPlay);
            const idx = this.currentCanPlayHandlers.indexOf(handleCanPlay);
            if (idx > -1) this.currentCanPlayHandlers.splice(idx, 1);
          }
        };

        this.video.addEventListener('canplay', handleCanPlay);
        this.currentCanPlayHandlers.push(handleCanPlay);

        if (this.video.readyState >= 3 && !this.isCancelled) handleCanPlay();
      });
    } catch (e) {
      console.error('🎬 [VideoController] 阿里云盘视频播放失败:', e);
      throw e;
    }
  }

  // 获取OneDrive视频URL
  async getOneDriveVideoUrl(songId) {
    try {
      console.log('🎬 [VideoController] 检查OneDrive状态...');
      
      // 1. 检查OneDrive状态
      const oneDriveStatus = await myRequest.get('/api/onedrive/status');
      console.log('🎬 [VideoController] OneDrive状态检查结果:', oneDriveStatus);
      
      if (oneDriveStatus === true) {
        console.log('🎬 [VideoController] OneDrive已连接，尝试获取OneDrive视频URL...');
        
        // 2. 获取OneDrive直接URL
        const oneDriveResponse = await myRequest.get(`/api/music/onedrive-direct-url/${songId}`);
        console.log('🎬 [VideoController] OneDrive视频资源请求结果:', oneDriveResponse);
        
        if (oneDriveResponse && oneDriveResponse.directUrl) {
          console.log('🎬 [VideoController] 获取到OneDrive视频URL:', oneDriveResponse.directUrl);
          return oneDriveResponse.directUrl;
        } else {
          console.warn('🎬 [VideoController] OneDrive视频资源请求失败，没有返回directUrl');
        }
      } else {
        console.log('🎬 [VideoController] OneDrive未连接或状态异常:', oneDriveStatus);
      }
      
      return null;
    } catch (error) {
      console.warn('🎬 [VideoController] OneDrive检查失败:', error);
      return null;
    }
  }

  /**
   * 是否为无效的占位 src（无 src 时浏览器会回落到当前页面 URL，如 /imusic）
   */
  isPlaceholderVideoSrc(src) {
    if (!src) return true;
    try {
      const resolved = new URL(src, window.location.href);
      if (resolved.origin !== window.location.origin) return false;
      const path = resolved.pathname || '';
      if (path.includes('/api/music/') || path.includes('/stream')) return false;
      if (/\.(mp4|webm|m4v|mov)(\?|$)/i.test(path)) return false;
      return true;
    } catch {
      return false;
    }
  }

  /**
   * 切换曲目前清理旧 src，避免对页面 URL 做 pause+load 引发 error/waiting
   */
  async clearVideoSrcBeforeLoad(expectedUrl) {
    // 切换到非 HLS 源前，先销毁可能存在的 hls.js 实例，避免 MediaSource 残留
    if (this._hls || this._usingHls) {
      this.destroyHls();
    }
    if (!this.video) return;
    const oldSrc = this.video.currentSrc || this.video.src;
    if (!oldSrc || oldSrc === expectedUrl) return;
    if (this.isPlaceholderVideoSrc(oldSrc)) {
      console.log('🎬 [VideoController] 清除无效占位视频源:', oldSrc);
      this.video.removeAttribute('src');
      this.video.load();
      return;
    }
    if (this.isCurrentSrcMatched()) return;
    console.log('🎬 [VideoController] 检测到旧视频源，先清除:', oldSrc);
    this.video.pause();
    this.video.removeAttribute('src');
    this.video.load();
    await new Promise(resolve => setTimeout(resolve, 50));
  }

  // 从 CDN 直链播放视频（原 OneDrive 命名保留）
  async playFromOneDrive(oneDriveUrl, startTime = 0, { deferAutoPlay = false } = {}) {
    try {
      console.log('🎬 [VideoController] 从OneDrive播放视频:', oneDriveUrl);
      console.log('🎬 [VideoController] 当前视频元素:', this.video);
      
      // 🔥 首先检查是否已取消，如果已取消则直接返回
      if (this.isCancelled) {
        console.log('🚫 [VideoController] OneDrive视频加载已取消，停止加载');
        throw new Error('视频加载已取消');
      }
      
      // 检查视频元素是否存在
      if (!this.video) {
        console.error('🎬 [VideoController] 视频元素不存在，尝试重新获取');
        this.getVideoElement();
        
        if (!this.video) {
          throw new Error('无法获取视频元素');
        }
      }
      
      // 🔥 保存当前加载的URL，用于后续验证
      const expectedSongId = this.currentSongId;
      const expectedUrl = oneDriveUrl;
      this.currentSongUrl = oneDriveUrl;
      
      // 🔥 再次检查是否已取消（在设置视频源之前）
      if (this.isCancelled || this.currentSongId !== expectedSongId) {
        console.log('🚫 [VideoController] OneDrive视频加载已取消或歌曲已切换，停止加载', {
          isCancelled: this.isCancelled,
          expectedSongId,
          currentSongId: this.currentSongId
        });
        throw new Error('视频加载已取消或歌曲已切换');
      }
      
      await this.clearVideoSrcBeforeLoad(expectedUrl);
      
      this._canplayEmittedForCurrentSong = false;
      // 🔥 设置新视频源到同一个视频元素（不创建新元素）
      this.video.src = oneDriveUrl;
      this.video.load();
      console.log('🎬 [VideoController] 新视频源已设置到同一个视频元素:', oneDriveUrl);
      
      if (startTime > 0) {
        this.video.currentTime = startTime;
      }
      
      console.log('🎬 [VideoController] OneDrive视频开始加载');
      
      // 🎬 等待视频加载完成后自动播放
      return new Promise((resolve, reject) => {
        // 🔥 检查是否已取消
        if (this.isCancelled) {
          console.log('🚫 [VideoController] OneDrive视频加载已取消，停止加载');
          reject(new Error('视频加载已取消'));
          return;
        }
        
        const handleCanPlay = async () => {
          // 🔥 检查是否已取消或歌曲已切换
          if (this.isCancelled) {
            console.log('🚫 [VideoController] OneDrive视频加载已取消，停止自动播放');
            this.video.removeEventListener('canplay', handleCanPlay);
            reject(new Error('视频加载已取消'));
            return;
          }
          
          if (!this.isCurrentSrcMatched() || this.currentSongId !== expectedSongId) {
            console.log('🚫 [VideoController] CDN 视频源已不匹配，停止加载', {
              videoSrc: this.video.src,
              expectedUrl,
              currentSongId: this.currentSongId,
              expectedSongId
            });
            this.video.removeEventListener('canplay', handleCanPlay);
            reject(new Error('视频源已不匹配'));
            return;
          }
          
          try {
            if (deferAutoPlay) {
              console.log('🎬 [VideoController] CDN 视频可播放，交由 PlaybackStrategyManager 统一 play()');
            } else {
              console.log('🎬 [VideoController] CDN 视频可以播放，开始自动播放');
              await this.play();
            }
            resolve();
          } catch (error) {
            console.error('🎬 [VideoController] CDN 视频加载后播放失败:', error);
            reject(error);
          } finally {
            this.video.removeEventListener('canplay', handleCanPlay);
            // 🔥 从监听器列表中移除
            const index = this.currentCanPlayHandlers.indexOf(handleCanPlay);
            if (index > -1) {
              this.currentCanPlayHandlers.splice(index, 1);
            }
          }
        };
        
        this.video.addEventListener('canplay', handleCanPlay);
        // 🔥 保存监听器引用，方便后续统一移除
        this.currentCanPlayHandlers.push(handleCanPlay);
        
        // 如果视频已经可以播放，立即触发
        if (this.video.readyState >= 3 && !this.isCancelled) {
          handleCanPlay();
        }
      });
    } catch (error) {
      console.error('🎬 [VideoController] OneDrive视频播放失败:', error);
      throw new Error('OneDrive视频播放失败');
    }
  }

  // 从视频缓存播放
  async playFromVideoCache(cacheResult, startTime = 0) {
    console.log('🎬 [VideoController] 从视频缓存播放:', cacheResult.videoUrl);
    
    try {
      // 🔥 首先检查是否已取消
      if (this.isCancelled) {
        console.log('🚫 [VideoController] 视频缓存加载已取消，停止加载');
        throw new Error('视频加载已取消');
      }
      
      // 🔥 保存当前加载的URL和歌曲ID，用于后续验证
      const expectedSongId = this.currentSongId;
      const expectedUrl = cacheResult.videoUrl;
      this.currentSongUrl = cacheResult.videoUrl;
      
      // 🔥 再次检查是否已取消（在设置视频源之前）
      if (this.isCancelled || this.currentSongId !== expectedSongId) {
        console.log('🚫 [VideoController] 视频缓存加载已取消或歌曲已切换，停止加载', {
          isCancelled: this.isCancelled,
          expectedSongId,
          currentSongId: this.currentSongId
        });
        throw new Error('视频加载已取消或歌曲已切换');
      }
      
      await this.clearVideoSrcBeforeLoad(expectedUrl);
      this._canplayEmittedForCurrentSong = false;
      
      // 🔥 设置新视频源到同一个视频元素（不创建新元素）
      this.video.src = cacheResult.videoUrl;
      this.currentBlobUrl = cacheResult.videoUrl;
      console.log('🎬 [VideoController] 新视频源已设置到同一个视频元素:', cacheResult.videoUrl);
      
      // 等待加载完成并自动播放
      await new Promise((resolve, reject) => {
        // 🔥 检查是否已取消
        if (this.isCancelled) {
          console.log('🚫 [VideoController] 视频缓存加载已取消，停止加载');
          reject(new Error('视频加载已取消'));
          return;
        }
        
        const handleCanPlay = async () => {
          // 🔥 检查是否已取消或歌曲已切换
          if (this.isCancelled) {
            console.log('🚫 [VideoController] 视频缓存加载已取消，停止自动播放');
            this.video.removeEventListener('canplay', handleCanPlay);
            reject(new Error('视频加载已取消'));
            return;
          }
          
          // 🔥 检查视频源是否还匹配当前歌曲
          if (this.video.src !== expectedUrl || this.currentSongId !== expectedSongId) {
            console.log('🚫 [VideoController] 视频缓存源已不匹配，停止自动播放', {
              videoSrc: this.video.src,
              expectedUrl,
              currentSongId: this.currentSongId,
              expectedSongId
            });
            this.video.removeEventListener('canplay', handleCanPlay);
            reject(new Error('视频源已不匹配'));
            return;
          }
          
          try {
            console.log('🎬 [VideoController] 视频缓存可以播放，开始自动播放');
            await this.play();
            console.log('🎬 [VideoController] 视频缓存自动播放成功');
            resolve();
          } catch (error) {
            console.error('🎬 [VideoController] 视频缓存自动播放失败:', error);
            reject(error);
          } finally {
            this.video.removeEventListener('canplay', handleCanPlay);
            // 🔥 从监听器列表中移除
            const index = this.currentCanPlayHandlers.indexOf(handleCanPlay);
            if (index > -1) {
              this.currentCanPlayHandlers.splice(index, 1);
            }
          }
        };
        
        this.video.addEventListener('canplay', handleCanPlay);
        // 🔥 保存监听器引用，方便后续统一移除
        this.currentCanPlayHandlers.push(handleCanPlay);
        this.video.onerror = reject;
        
        // 如果视频已经可以播放，立即触发
        if (this.video.readyState >= 3 && !this.isCancelled) {
          handleCanPlay();
        }
      });
      
      console.log('🎬 [VideoController] 视频缓存播放准备完成');
      return true;
      
    } catch (error) {
      console.error('🎬 [VideoController] 视频缓存播放失败:', error);
      throw error;
    }
  }

  // 从CDN播放
  async playFromCDN(url, startTime = 0) {
    console.log('🎬 [VideoController] 从CDN播放视频:', url);
    
    try {
      // 🔥 修复：检查URL是否有效
      if (!url || url === 'undefined') {
        throw new Error('视频URL无效');
      }
      
      // 🔥 首先检查是否已取消
      if (this.isCancelled) {
        console.log('🚫 [VideoController] CDN视频加载已取消，停止加载');
        throw new Error('视频加载已取消');
      }
      
      // 🔥 修复：确保视频元素存在
      if (!this.video) {
        console.log('🎬 [VideoController] playFromCDN: video 元素不存在，尝试获取或创建');
        this.video = this.getVideoElement();
        if (!this.video) {
          throw new Error('无法获取或创建视频元素');
        }
      }
      
      // 🔥 保存当前加载的URL和歌曲ID，用于后续验证
      const expectedSongId = this.currentSongId;
      const expectedUrl = url;
      this.currentSongUrl = url;
      
      // 🔥 再次检查是否已取消（在设置视频源之前）
      if (this.isCancelled || this.currentSongId !== expectedSongId) {
        console.log('🚫 [VideoController] CDN视频加载已取消或歌曲已切换，停止加载', {
          isCancelled: this.isCancelled,
          expectedSongId,
          currentSongId: this.currentSongId
        });
        throw new Error('视频加载已取消或歌曲已切换');
      }
      
      await this.clearVideoSrcBeforeLoad(expectedUrl);
      this._canplayEmittedForCurrentSong = false;
      
      // 🔥 设置新视频源到同一个视频元素（不创建新元素）
      this.video.src = url;
      console.log('🎬 [VideoController] 新视频源已设置到同一个视频元素:', url);
      
      // 🔥 修复：等待 canplay 事件并成功播放后才 resolve，确保与 playFromOneDrive 和 playFromVideoCache 行为一致
      await new Promise((resolve, reject) => {
        let canPlayHandler = null; // 🔥 提前声明，让超时处理可以访问
        
        // 🔥 修复：添加超时机制（在 Promise 内部，可以访问 reject）
        const loadTimeout = setTimeout(() => {
          console.error('🎬 [VideoController] CDN视频加载超时');
          if (this.video) {
            this.video.oncanplay = null;
          }
          // 🔥 从监听器列表中移除
          if (canPlayHandler) {
            const index = this.currentCanPlayHandlers.indexOf(canPlayHandler);
            if (index > -1) {
              this.currentCanPlayHandlers.splice(index, 1);
            }
          }
          reject(new Error('视频加载超时'));
        }, 10000); // 10秒超时
        // 🔥 检查是否已取消
        if (this.isCancelled) {
          console.log('🚫 [VideoController] CDN视频加载已取消，停止加载');
          clearTimeout(loadTimeout);
          reject(new Error('视频加载已取消'));
          return;
        }
        
        // 🔥 设置 currentTime（在 loadedmetadata 时）
        this.video.onloadedmetadata = () => {
          if (this.isCancelled) {
            console.log('🚫 [VideoController] CDN视频加载已取消，忽略元数据加载完成');
            clearTimeout(loadTimeout);
            reject(new Error('视频加载已取消'));
            return;
          }
          this.video.currentTime = startTime;
          console.log('🎬 [VideoController] 视频元数据加载完成');
        };
        
        this.video.onerror = (error) => {
          clearTimeout(loadTimeout);
          console.error('🎬 [VideoController] 视频加载错误:', error);
          reject(new Error('视频加载失败'));
        };
        
        canPlayHandler = async () => {
          // 🔥 检查是否已取消或歌曲已切换
          if (this.isCancelled) {
            console.log('🚫 [VideoController] CDN视频加载已取消，停止自动播放');
            this.video.oncanplay = null;
            clearTimeout(loadTimeout);
            reject(new Error('视频加载已取消'));
            return;
          }
          
          // 🔥 检查视频源是否还匹配当前歌曲
          if (this.video.src !== expectedUrl || this.currentSongId !== expectedSongId) {
            console.log('🚫 [VideoController] CDN视频源已不匹配，停止自动播放', {
              videoSrc: this.video.src,
              expectedUrl,
              currentSongId: this.currentSongId,
              expectedSongId
            });
            this.video.oncanplay = null;
            clearTimeout(loadTimeout);
            reject(new Error('视频源已不匹配'));
            return;
          }
          
          try {
            console.log('🎬 [VideoController] CDN视频可以播放，开始自动播放');
            await this.play();
            console.log('🎬 [VideoController] CDN视频自动播放成功');
            clearTimeout(loadTimeout);
            // 🔥 关键修复：在成功播放后才 resolve Promise
            this.video.oncanplay = null;
            // 🔥 从监听器列表中移除
            const index = this.currentCanPlayHandlers.indexOf(canPlayHandler);
            if (index > -1) {
              this.currentCanPlayHandlers.splice(index, 1);
            }
            resolve();
          } catch (error) {
            console.error('🎬 [VideoController] CDN视频自动播放失败:', error);
            clearTimeout(loadTimeout);
            this.video.oncanplay = null;
            reject(error);
          }
        };
        
        this.video.oncanplay = canPlayHandler;
        // 🔥 保存监听器引用（作为函数引用保存，因为 oncanplay 是属性）
        this.currentCanPlayHandlers.push(canPlayHandler);
        
        // 🔥 如果视频已经可以播放，立即触发
        if (this.video.readyState >= 3 && !this.isCancelled) {
          canPlayHandler();
        }
      });
      
      // 启动视频缓存
      this.startVideoCache(url);
      
      console.log('🎬 [VideoController] CDN视频播放准备完成');
      return true;
      
    } catch (error) {
      console.error('🎬 [VideoController] CDN视频播放失败:', error);
      throw error;
    }
  }

  // 启动视频缓存
  startVideoCache(url) {
    if (!isVideoSongAutoCacheEnabled()) {
      console.log('🎬 [VideoController] 视频版自动缓存已关闭，跳过');
      return;
    }
    console.log('🎬 [VideoController] 启动视频缓存');
    
    this.isVideoCaching = true;
    this.videoCacheStatus = '开始缓存视频...';
    this.videoCacheProgress = 0;
    
    // 使用统一视频缓存管理器
    this.performVideoCache(url);
  }

  // 执行视频缓存
  async performVideoCache(url) {
    try {
      const userId = tokenService.getCurrentUserId();
      const songId = extractSongIdFromUrl(url);
      
      console.log('🎬 [VideoController] 执行视频缓存:', songId);
      
      // 使用统一视频缓存管理器
      await unifiedVideoCacheManager.cacheVideo(userId, songId, url, {
        onProgress: (progress) => {
          this.videoCacheProgress = progress;
          this.videoCacheStatus = `缓存进度: ${Math.round(progress)}%`;
          this.emit('videocaching', { progress, status: this.videoCacheStatus });
        },
        onComplete: () => {
          this.isVideoCaching = false;
          this.videoCacheStatus = '视频缓存完成';
          this.videoCacheProgress = 100;
          this.emit('videocaching', { progress: 100, status: this.videoCacheStatus });
          console.log('🎬 [VideoController] 视频缓存完成');
        },
        onError: (error) => {
          this.isVideoCaching = false;
          this.videoCacheStatus = '视频缓存失败';
          console.error('🎬 [VideoController] 视频缓存失败:', error);
          this.emit('videocaching', { progress: 0, status: this.videoCacheStatus, error });
        }
      });
      
    } catch (error) {
      console.error('🎬 [VideoController] 视频缓存失败:', error);
      this.isVideoCaching = false;
      this.videoCacheStatus = '视频缓存失败';
      this.emit('videocaching', { progress: 0, status: this.videoCacheStatus, error });
    }
  }

  // 播放
  async play() {
    // 🔥 检查是否已取消，如果已取消则不播放
    if (this.isCancelled) {
      console.log('🚫 [VideoController] 视频播放已取消，阻止播放');
      return false;
    }
    
    if (this.video) {
      try {
        await this.video.play();
        console.log('🎬 [VideoController] 视频播放开始');
        this.emit('play');
        return true;
      } catch (error) {
        console.error('🎬 [VideoController] 视频播放失败:', error);
        throw error;
      }
    }
    return false;
  }

  // 暂停
  pause() {
    if (this.video) {
      this.video.pause();
      console.log('🎬 [VideoController] 视频播放暂停');
      this.emit('pause');
    }
  }

  // 停止
  stop() {
    if (this.video) {
      this.video.pause();
      this.video.currentTime = 0;
      console.log('🎬 [VideoController] 视频播放停止');
    }
  }

  // 设置播放时间
  setCurrentTime(time) {
    if (this.video) {
      this.video.currentTime = time;
    }
  }

  // 获取播放时间
  getCurrentTime() {
    return this.video ? this.video.currentTime : 0;
  }

  // 获取总时长
  getDuration() {
    return this.video ? this.video.duration : 0;
  }

  // 获取播放状态
  isPlaying() {
    return this.video ? !this.video.paused : false;
  }

  // 设置音量
  setVolume(volume) {
    if (this.video) {
      this.video.volume = Math.max(0, Math.min(1, volume));
    }
  }

  // 获取音量
  getVolume() {
    return this.video ? this.video.volume : 1;
  }

  // 设置静音
  setMuted(muted) {
    if (this.video) {
      this.video.muted = muted;
    }
  }

  // 获取静音状态
  isMuted() {
    return this.video ? this.video.muted : true;
  }

  // 获取视频元素（用于DOM操作）
  getVideoElement() {
    return this.video;
  }

  // 🔥 新增：清理前一首歌的资源（更彻底的清理）
  async cleanupPreviousSong() {
    console.log('🎬 [VideoController] 清理前一首歌的资源');
    
    try {
      // 🔥 首先设置取消标志，阻止所有正在进行的加载操作
      this.isCancelled = true;
      console.log('🚫 [VideoController] 设置取消标志，阻止加载操作');
      
      // 🔥 销毁可能存在的 hls.js 实例（HLS 播放场景）
      this.destroyHls();
      
      // 🔥 移除所有 canplay 监听器，防止自动播放
      if (this.video && this.currentCanPlayHandlers && this.currentCanPlayHandlers.length > 0) {
        console.log('🚫 [VideoController] 移除所有 canplay 监听器，数量:', this.currentCanPlayHandlers.length);
        this.currentCanPlayHandlers.forEach(handler => {
          try {
            this.video.removeEventListener('canplay', handler);
          } catch (error) {
            console.warn('⚠️ [VideoController] 移除 canplay 监听器时出错:', error);
          }
        });
        this.currentCanPlayHandlers = [];
      }
      
      // 🔥 清除 oncanplay 属性（用于 CDN 播放）
      if (this.video) {
        try {
          this.video.oncanplay = null;
        } catch (error) {
          console.warn('⚠️ [VideoController] 清除 oncanplay 时出错:', error);
        }
      }
      
      // 1. 停止当前播放
      if (this.video) {
        this.video.pause();
        this.video.currentTime = 0;
        console.log('🎬 [VideoController] 已停止当前视频播放');
      }
      
      // 2. 清理所有事件监听器
      if (this.video) {
        this.video.removeEventListener('timeupdate', this.handleTimeUpdate);
        this.video.removeEventListener('ended', this.handleEnded);
        this.video.removeEventListener('play', this.handlePlay);
        this.video.removeEventListener('pause', this.handlePause);
        this.video.removeEventListener('error', this.handleError);
        this.video.removeEventListener('loadedmetadata', this.handleLoadedMetadata);
        this.video.removeEventListener('canplay', this.handleCanPlay);
        this.video.removeEventListener('progress', this.handleProgress);
        this.video.removeEventListener('waiting', this.handleWaiting);
        this.video.removeEventListener('playing', this.handlePlaying);
        console.log('🎬 [VideoController] 已清理所有事件监听器');
      }
      
      // 3. 清理Blob URL
      if (this.currentBlobUrl) {
        URL.revokeObjectURL(this.currentBlobUrl);
        this.currentBlobUrl = null;
        console.log('🎬 [VideoController] 已清理Blob URL');
      }
      
      // 4. 清理定时器
      if (this.streamingTimeout) {
        clearTimeout(this.streamingTimeout);
        this.streamingTimeout = null;
        console.log('🎬 [VideoController] 已清理定时器');
      }
      
      // 5. 重置视频元素状态
      // ⚠️ 注意：这里只清理同一个视频元素的 src，不会创建新元素，保持自动播放权限
      if (this.video) {
        const oldSrc = this.video.src;
        if (oldSrc) {
          console.log('🎬 [VideoController] 清理旧视频源:', oldSrc);
        }
        // 🔥 只清理 src，不创建新元素，保持 this.video 引用不变
        this.video.pause();
        this.video.currentTime = 0;
        this.video.src = '';
        this.video.load(); // 重置视频元素（同一个元素）
        // 🔥 清除时长信息，防止残留
        this.video.duration = NaN;
        // 🔥 等待一小段时间，确保浏览器有时间处理清理操作
        await new Promise(resolve => setTimeout(resolve, 50));
        console.log('🎬 [VideoController] 已重置视频元素状态和时长信息（使用同一个视频元素）');
      }
      
      // 6. 重置所有状态变量
      this.currentSongUrl = null;
      this.currentSongId = null;
      this.currentUserId = null;
      this.isVideoLoading = false;
      this.isVideoCaching = false;
      this.videoCacheStarted = false;
      this.videoCacheProgress = 0;
      this.videoCacheStatus = '';
      this.retryCount = 0;
      console.log('🎬 [VideoController] 已重置所有状态变量');
      
      // 🔥 关键修复：发送 videoloading 事件，通知 UI 清除加载状态
      // 即使当前不是视频模式，也要清除加载状态，防止残留
      this.emit('videoloading', { loading: false });
      console.log('🎬 [VideoController] 已发送 videoloading 事件，通知 UI 清除加载状态');
      
      // 7. 🔥 关键修复：不清空事件回调集合！
      // 因为 VideoStrategy 在 constructor 中已经注册了事件监听器
      // 如果清空 eventCallbacks，会导致事件监听器丢失，playing 事件无法传播
      // VideoStrategy 会在每次创建时重新注册事件监听器，所以不需要清空
      // 清理前一首歌时，只需要清理视频元素和状态，不需要清空事件回调
      console.log('🎬 [VideoController] 保留事件回调集合，避免丢失事件监听器');
      
      console.log('✅ [VideoController] 前一首歌资源清理完成');
      
    } catch (error) {
      console.error('❌ [VideoController] 清理前一首歌资源失败:', error);
      // 不抛出错误，继续执行
    }
  }

  // 清理资源
  cleanup() {
    console.log('🎬 [VideoController] 清理资源');
    
    // 清理定时器
    if (this.streamingTimeout) {
      clearTimeout(this.streamingTimeout);
      this.streamingTimeout = null;
    }
    
    // 销毁 hls.js 实例
    this.destroyHls();
    
    // 清理视频元素
    if (this.video) {
      this.video.pause();
      this.video.src = '';
      this.video = null;
    }
    
    // 清理Blob URL
    if (this.currentBlobUrl) {
      URL.revokeObjectURL(this.currentBlobUrl);
      this.currentBlobUrl = null;
    }
    
    // 重置状态
    this.currentSongUrl = null;
    this.currentSongId = null;
    this.isVideoLoading = false;
    this.isVideoCaching = false;
    this.videoCacheProgress = 0;
    this.videoCacheStatus = '';
  }

  // 事件处理方法
  handleTimeUpdate() {
    // 🔥 修复：添加 null 检查，防止在清理过程中访问 null 的 video 元素
    if (!this.video) {
      return;
    }
    this.emit('timeupdate', {
      currentTime: this.video.currentTime,
      duration: this.video.duration
    });
  }

  handleEnded() {
    console.log('🎬 [VideoController] 视频播放结束');
    this.emit('ended');
  }

  handlePlay() {
    console.log('🎬 [VideoController] 视频播放事件');
    this.emit('play');
  }

  handlePause() {
    // 播到结尾时浏览器也会发 pause，ended 仍为 true。这条不能当成用户暂停，否则单曲循环重播后按钮会停在暂停。
    if (this.video?.ended) {
      console.log('🎬 [VideoController] 视频已结束触发的 pause，交给 ended 处理');
      return;
    }
    console.log('🎬 [VideoController] 视频暂停事件');
    this.emit('pause');
  }

  handleError(event) {
    // 🔥 切歌清理（isCancelled）或正在加载新源（_loadingNewSource）期间，
    // <video> 会因清空/切换 src、撤销 blob 而抛出瞬时 error，这些不是真实播放失败，
    // 不能外抛，否则会触发 DXHPlayer 的重试 → 反复重建控制器 → error 监听器累加的死循环。
    if (this.isCancelled || this._loadingNewSource) {
      console.log('🚫 [VideoController] 切换/加载中产生的瞬时视频 error，已忽略');
      return;
    }
    // 没有真实 MediaError 对象时（如 emptied/abort 派生的 error）也忽略
    if (this.video && !this.video.error) {
      console.log('🚫 [VideoController] 无 MediaError 的 error 事件，已忽略');
      return;
    }
    console.error('🎬 [VideoController] 视频错误:', event);
    this.emit('error', { error: '视频播放错误' });
  }

  /** 公开方法：供外部（如 DXHPlayer）查询当前是否在加载新源（用于判定瞬时 error） */
  isLoadingNewSource() {
    return this._loadingNewSource || this.isCancelled;
  }

  /**
   * 判断 <video>.src 是否仍匹配当前歌曲源。
   * 浏览器会把相对路径（/api/music/xxx/stream）补全成绝对路径（http://host/api/music/xxx/stream），
   * 直接用 === 比较会误判不匹配。这里做宽松匹配：绝对路径以相对路径结尾即视为匹配。
   */
  isCurrentSrcMatched() {
    if (!this.currentSongUrl || !this.video) return true;
    const src = this.video.currentSrc || this.video.src;
    if (!src || this.isPlaceholderVideoSrc(src)) return false;
    const expected = this.currentSongUrl;
    if (src === expected) return true;
    try {
      if (decodeURIComponent(src) === decodeURIComponent(expected)) return true;
    } catch (_) { /* ignore */ }
    try {
      const a = new URL(src);
      const b = new URL(expected);
      if (a.origin === b.origin && a.pathname === b.pathname) return true;
    } catch (_) { /* ignore */ }
    if (expected.startsWith('/') && src.includes(expected)) return true;
    return false;
  }

  handleLoadedMetadata() {
    // 🔥 检查是否已取消或视频源是否匹配当前歌曲
    if (this.isCancelled) {
      console.log('🚫 [VideoController] 视频元数据加载事件已取消，忽略');
      return;
    }
    
    // 🔥 检查视频源是否还匹配当前歌曲URL（宽松匹配，兼容相对/绝对路径）
    if (!this.isCurrentSrcMatched()) {
      console.log('🚫 [VideoController] 视频源已不匹配，忽略 loadedmetadata 事件', {
        videoSrc: this.video.src,
        currentSongUrl: this.currentSongUrl,
        currentSongId: this.currentSongId
      });
      return;
    }
    
    console.log('🎬 [VideoController] 视频元数据加载完成');
    this.emit('loadedmetadata', {
      duration: this.video.duration
    });
  }

  handleCanPlay() {
    // 🔥 检查是否已取消或视频源是否匹配当前歌曲
    if (this.isCancelled) {
      console.log('🚫 [VideoController] 视频可以播放事件已取消，忽略');
      return;
    }
    
    // 🔥 检查视频源是否还匹配当前歌曲URL（宽松匹配，兼容相对/绝对路径）
    if (!this.isCurrentSrcMatched()) {
      console.log('🚫 [VideoController] 视频源已不匹配，忽略 canplay 事件', {
        videoSrc: this.video.src,
        currentSongUrl: this.currentSongUrl,
        currentSongId: this.currentSongId
      });
      return;
    }
    
    // 源已就绪，结束“加载新源”窗口，此后的 error 视为真实播放错误
    this._loadingNewSource = false;
    if (this._canplayEmittedForCurrentSong) {
      return;
    }
    this._canplayEmittedForCurrentSong = true;
    console.log('🎬 [VideoController] 视频可以播放');
    this.emit('canplay');
  }

  handleProgress() {
    this.emit('progress');
  }

  handleWaiting() {
    // 🔥 检查是否已取消，如果已取消则不触发 waiting 事件
    if (this.isCancelled) {
      console.log('🚫 [VideoController] 视频等待数据事件已取消，忽略');
      return;
    }
    
    // 🔥 检查视频源是否还匹配当前歌曲URL（宽松匹配，兼容相对/绝对路径）
    if (!this.isCurrentSrcMatched()) {
      console.log('🚫 [VideoController] 视频源已不匹配，忽略 waiting 事件', {
        videoSrc: this.video.src,
        currentSongUrl: this.currentSongUrl,
        currentSongId: this.currentSongId
      });
      return;
    }
    
    console.log('🎬 [VideoController] 视频等待数据');
    this.emit('waiting');
  }

  handlePlaying() {
    console.log('🎬 [VideoController] 🔥 视频正在播放事件触发');
    console.log('🎬 [VideoController] 视频播放状态:', {
      paused: this.video?.paused,
      readyState: this.video?.readyState,
      currentTime: this.video?.currentTime
    });
    
    // 🔥 关键修复：直接更新全局播放状态，确保UI立即响应
    // 这是双重保障：既保留事件机制，也直接更新状态，确保即使事件监听器丢失也能更新
    try {
      // 动态导入 PlaybackStore，避免循环依赖
      import('@/store/playback.js').then(({ usePlaybackStore }) => {
        const playbackStore = usePlaybackStore();
        console.log('🎬 [VideoController] 🔥 直接更新全局播放状态为 true');
        playbackStore.setPlaying(true);
      }).catch(error => {
        console.warn('🎬 [VideoController] 无法导入 PlaybackStore，使用事件机制:', error);
      });
    } catch (error) {
      console.warn('🎬 [VideoController] 更新全局状态失败，使用事件机制:', error);
    }
    
    // 确保 playing 事件正确触发并传播到策略层（事件机制作为备用）
    this.emit('playing');
  }

  handleLoadStart() {
    // 🔥 检查是否已取消，如果已取消则不触发 loadstart 事件
    if (this.isCancelled) {
      console.log('🚫 [VideoController] 视频开始加载事件已取消，忽略');
      return;
    }
    
    // 🔥 检查视频源是否还匹配当前歌曲URL（宽松匹配，兼容相对/绝对路径）
    if (!this.isCurrentSrcMatched()) {
      console.log('🚫 [VideoController] 视频源已不匹配，忽略 loadstart 事件', {
        videoSrc: this.video.src,
        currentSongUrl: this.currentSongUrl,
        currentSongId: this.currentSongId
      });
      return;
    }
    
    console.log('🎬 [VideoController] 视频开始加载');
    this.isVideoLoading = true;
    this.emit('videoloading', { loading: true });
  }

  handleCanPlayThrough() {
    console.log('🎬 [VideoController] 视频可以流畅播放');
    this.isVideoLoading = false;
    this.emit('videoloading', { loading: false });
  }

  handlePageUnload() {
    console.log('🎬 [VideoController] 页面卸载，清理资源');
    this.cleanup();
  }

  // 事件系统
  on(event, callback) {
    if (this.eventCallbacks[event]) {
      this.eventCallbacks[event].push(callback);
    }
  }

  off(event, callback) {
    if (this.eventCallbacks[event]) {
      const index = this.eventCallbacks[event].indexOf(callback);
      if (index > -1) {
        this.eventCallbacks[event].splice(index, 1);
      }
    }
  }

  emit(event, data) {
    if (this.eventCallbacks[event]) {
      this.eventCallbacks[event].forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error('🎬 [VideoController] 事件回调错误:', error);
        }
      });
    }
  }
}

export default VideoController;
